// FAQ Help Center — tabbed topic switcher.
// Progressive enhancement: without JS the first panel stays visible.
class FaqHelpCenter extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (this.tabs.length === 0) return;

    const { signal } = this.#controller;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });

    this.tablist = this.querySelector('[role="tablist"]');
    if (this.tablist) this.#initDragScroll(this.tablist, signal);

    // Ensure a single active tab on load (defaults to first).
    const active = this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.select(active < 0 ? 0 : active);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    if (!target) return;

    event.preventDefault();
    this.select(this.tabs.indexOf(target));
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    const current = this.tabs.findIndex((tab) => tab === document.activeElement);
    if (current < 0) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next);
    this.tabs[next]?.focus();
  };

  // Mouse drag-to-scroll for the tab bar when it overflows on desktop.
  // Touch devices keep their native horizontal scrolling.
  #drag = { active: false, moved: false, startX: 0, startScroll: 0, pointerId: -1 };

  /**
   * @param {HTMLElement} el
   * @param {AbortSignal} signal
   */
  #initDragScroll(el, signal) {
    el.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.#drag = {
          active: true,
          moved: false,
          startX: event.clientX,
          startScroll: el.scrollLeft,
          pointerId: event.pointerId,
        };
      },
      { signal }
    );

    el.addEventListener(
      'pointermove',
      (event) => {
        if (!this.#drag.active) return;
        const dx = event.clientX - this.#drag.startX;
        // Only start capturing/scrolling once it's clearly a drag, so plain
        // clicks aren't retargeted away from the tab buttons.
        if (!this.#drag.moved && Math.abs(dx) > 4) {
          this.#drag.moved = true;
          el.classList.add('is-dragging');
          el.setPointerCapture(this.#drag.pointerId);
        }
        if (this.#drag.moved) {
          el.scrollLeft = this.#drag.startScroll - dx;
        }
      },
      { signal }
    );

    const end = () => {
      if (!this.#drag.active) return;
      if (this.#drag.pointerId !== -1 && el.hasPointerCapture(this.#drag.pointerId)) {
        el.releasePointerCapture(this.#drag.pointerId);
      }
      this.#drag.active = false;
      el.classList.remove('is-dragging');
    };

    el.addEventListener('pointerup', end, { signal });
    el.addEventListener('pointercancel', end, { signal });

    // Swallow the click that ends a drag so it doesn't switch tabs.
    el.addEventListener(
      'click',
      (event) => {
        if (this.#drag.moved) {
          event.preventDefault();
          event.stopPropagation();
          this.#drag.moved = false;
        }
      },
      { capture: true, signal }
    );
  }

  /** @param {number} index */
  select(index) {
    this.tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.tabIndex = isActive ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }
}

if (!customElements.get('faq-help-center')) {
  customElements.define('faq-help-center', FaqHelpCenter);
}
